/**
 * Row shaping, totals and period comparison for the uploaded-report SQPR dashboard.
 *
 * Every metric the dashboard shows is listed in `AGGREGATIONS` with how it rolls up across search queries. The
 * totals and the comparison both iterate that table, so a metric that is not in it is neither totalled nor compared
 * (which is what keeps array fields such as `adv_targets` out of the comparison).
 */

import { type SearchTermStatsRow, type SQPReportRow } from "../../zodios/apiDefinitions/api/sqpReports";

type Metrics = Record<string, number | null | undefined>;

export interface SQPRRow {
  search_query: string;
  adv_targets?: (number | null)[];
  adv_keywords?: (number | null)[];
  [metric: string]: number | null | undefined | string | (number | null)[];
}

type Aggregation =
  | { kind: "sum" }
  | { kind: "mean" }
  /** A median per query cannot be combined into a median across queries, so there is no total. */
  | { kind: "none" }
  | { kind: "ratio"; numerator: string; denominator: string }
  /** `minuend - subtrahend`, both of them metrics totalled first. */
  | { kind: "difference"; minuend: string; subtrahend: string };

const sum = { kind: "sum" } as const;
const none = { kind: "none" } as const;
const ratio = (numerator: string, denominator: string) => ({ kind: "ratio", numerator, denominator }) as const;

// Rates, shares, CTR and CV match the v4 SQPR query in app-rest-api (api/query/search_query_performance_report.py).
const AGGREGATIONS: Record<string, Aggregation> = {
  search_query_score: { kind: "mean" },
  search_query_volume: sum,

  impressions_total: sum,
  impressions_brand_or_asin: sum,
  impressions_brand_or_asin_share: ratio("impressions_brand_or_asin", "impressions_total"),

  clicks_total: sum,
  clicks_total_rate: ratio("clicks_total", "search_query_volume"),
  clicks_brand_or_asin: sum,
  clicks_brand_or_asin_share: ratio("clicks_brand_or_asin", "clicks_total"),
  clicks_price_median: none,
  clicks_brand_or_asin_price_median: none,
  // Shipping speeds are counts (clicks on results offering that speed), so they add up.
  clicks_same_day_shipping_speed: sum,
  clicks_1d_shipping_speed: sum,
  clicks_2d_shipping_speed: sum,

  cart_adds_total: sum,
  cart_adds_total_rate: ratio("cart_adds_total", "search_query_volume"),
  cart_adds_brand_or_asin: sum,
  cart_adds_brand_or_asin_share: ratio("cart_adds_brand_or_asin", "cart_adds_total"),
  cart_adds_price_median: none,
  cart_adds_brand_or_asin_price_median: none,
  cart_adds_same_day_shipping_speed: sum,
  cart_adds_1d_shipping_speed: sum,
  cart_adds_2d_shipping_speed: sum,

  purchases_total: sum,
  purchases_total_rate: ratio("purchases_total", "search_query_volume"),
  purchases_brand_or_asin: sum,
  purchases_brand_or_asin_share: ratio("purchases_brand_or_asin", "purchases_total"),
  purchases_price_median: none,
  purchases_brand_or_asin_price_median: none,
  purchases_same_day_shipping_speed: sum,
  purchases_1d_shipping_speed: sum,
  purchases_2d_shipping_speed: sum,

  ctr_total: ratio("clicks_total", "impressions_total"),
  ctr_brand_or_asin: ratio("clicks_brand_or_asin", "impressions_brand_or_asin"),
  cv_total: ratio("purchases_total", "clicks_total"),
  cv_brand_or_asin: ratio("purchases_brand_or_asin", "clicks_brand_or_asin"),
  ctr_difference: { kind: "difference", minuend: "ctr_brand_or_asin", subtrahend: "ctr_total" },
  cv_difference: { kind: "difference", minuend: "cv_brand_or_asin", subtrahend: "cv_total" },

  // Ads stats from the search term report.
  ad_spend: sum,
  ad_sales: sum,
  acos: ratio("ad_spend", "ad_sales"),
  roas: ratio("ad_sales", "ad_spend"),
  clicks: sum,
  impressions: sum,
};

/** Metrics the search term report contributes per query. */
const AD_STAT_KEYS = ["ad_spend", "ad_sales", "acos", "roas", "clicks", "impressions"] as const;

/** Metrics a row does not carry and gets from its own components, the same way the totals do. */
const ROW_DERIVED_METRICS = ["ctr_total", "ctr_brand_or_asin", "cv_total", "cv_brand_or_asin"] as const;
const ROW_DIFFERENCE_METRICS = ["ctr_difference", "cv_difference"] as const;

const toNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === "") return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/** A metric's value, or null when it is missing (or, on a row, is not a metric). */
const metric = (metrics: Metrics | SQPRRow, id: string) => {
  const value = metrics[id];
  return typeof value === "number" ? value : null;
};

const divide = (numerator: number | null, denominator: number | null) =>
  numerator != null && denominator ? numerator / denominator : null;

const subtract = (minuend: number | null, subtrahend: number | null) =>
  minuend != null && subtrahend != null ? minuend - subtrahend : null;

const applyDerived = (metrics: Metrics | SQPRRow, ids: readonly string[]) => {
  for (const id of ids) {
    const aggregation = AGGREGATIONS[id];
    if (aggregation?.kind === "ratio") {
      metrics[id] = divide(metric(metrics, aggregation.numerator), metric(metrics, aggregation.denominator));
    } else if (aggregation?.kind === "difference") {
      metrics[id] = subtract(metric(metrics, aggregation.minuend), metric(metrics, aggregation.subtrahend));
    }
  }
};

/**
 * Normalises report rows and joins the ads stats for the same query: Amazon's `%` columns become fractions, CTR, CV
 * and their brand-vs-total differences are derived, and ads stats are joined on the query text.
 */
export function shapeSQPRows(rows: SQPReportRow[], searchTermStats: SearchTermStatsRow[] | undefined): SQPRRow[] {
  const statsByQuery = new Map((searchTermStats ?? []).map((stats) => [stats.query, stats]));
  return rows.map((row) => {
    const values: Record<string, unknown> = row;
    const shaped: SQPRRow = { search_query: row.search_query };
    for (const id of Object.keys(AGGREGATIONS)) {
      if (!(id in values)) continue;
      const value = toNumber(values[id]);
      shaped[id] = value != null && (id.endsWith("_share") || id.endsWith("_rate")) ? value / 100 : value;
    }
    applyDerived(shaped, ROW_DERIVED_METRICS);
    applyDerived(shaped, ROW_DIFFERENCE_METRICS);

    const stats = statsByQuery.get(row.search_query);
    if (stats) {
      for (const id of AD_STAT_KEYS) shaped[id] = toNumber(stats[id]);
      shaped.adv_targets = stats.adv_targets;
      shaped.adv_keywords = stats.adv_keywords;
    }
    return shaped;
  });
}

/** Rolls the rows up into one total per metric, following `AGGREGATIONS`. Metrics with no meaningful total are absent. */
function summarizeSQPRows(rows: SQPRRow[]): Metrics {
  const total: Metrics = {};
  const derived: string[] = [];
  const differences: string[] = [];
  for (const [id, aggregation] of Object.entries(AGGREGATIONS)) {
    if (aggregation.kind === "sum" || aggregation.kind === "mean") {
      const values = rows.map((row) => metric(row, id)).filter((value) => value != null);
      if (!values.length) continue;
      const sumOfValues = values.reduce((acc, value) => acc + value, 0);
      total[id] = aggregation.kind === "sum" ? sumOfValues : sumOfValues / values.length;
    } else if (aggregation.kind === "ratio") {
      derived.push(id);
    } else if (aggregation.kind === "difference") {
      differences.push(id);
    }
  }
  applyDerived(total, derived);
  applyDerived(total, differences);
  return total;
}

/** Adds `<id>_prev`, `<id>_delta` and `<id>_percent` for every metric both sides have a value for. */
function compareMetrics<T extends Metrics | SQPRRow>(current: T, previous: Metrics | SQPRRow | undefined): T {
  if (!previous) return current;
  const compared: T = { ...current };
  for (const id of Object.keys(AGGREGATIONS)) {
    const value = metric(current, id);
    const previousValue = metric(previous, id);
    if (value == null || previousValue == null) continue;
    const delta = value - previousValue;
    Object.assign(compared, {
      [`${id}_prev`]: previousValue,
      [`${id}_delta`]: delta,
      [`${id}_percent`]: previousValue ? delta / previousValue : 0,
    });
  }
  return compared;
}

/**
 * Builds what the dashboard renders: per-query rows and the total, each compared against `compareRows` when there is
 * a compare report, plus the compare report's queries that the selected report does not have.
 */
export function compareSQPRows(rows: SQPRRow[], compareRows: SQPRRow[] | undefined) {
  const total = summarizeSQPRows(rows);
  if (!compareRows) {
    return { rows, total, missingQueries: [] as string[] };
  }
  const compareByQuery = new Map(compareRows.map((row) => [row.search_query, row]));
  const queries = new Set(rows.map((row) => row.search_query));
  return {
    rows: rows.map((row) => compareMetrics(row, compareByQuery.get(row.search_query))),
    total: compareMetrics(total, summarizeSQPRows(compareRows)),
    missingQueries: compareRows.map((row) => row.search_query).filter((query) => !queries.has(query)),
  };
}
